// @ts-nocheck
import cv, { Mat } from 'opencv-ts'
import {downloadModel, ensureModel, getModel, modelExists} from "../../../utils/model";
import {ModelTypeConstant} from "../../../constants";
import ort from "onnxruntime-web/all";
import {prettierLog} from "../../../utils";
import {getBrowserCapabilities} from "../../../utils/browserUtil";
import {blobToArrayBuffer, loadImage} from "../../../utils/filesUtil";

let model: any = null;

let modelBasicInfo = getModel(ModelTypeConstant.SUPER_RESOLUTION);

let targetWidth = modelBasicInfo.targetWidth;
let targetHeight = modelBasicInfo.targetHeight;
let inputName = modelBasicInfo.inputName;
let modelScale = modelBasicInfo.scale;

let loadingNotification = () => {};
const configEnv = (capabilities: {
    webGPU: boolean;
    wasm?: boolean;
    simd: any;
    threads: any;
}) => {
    if (capabilities.webGPU) {
        ort.env.wasm.numThreads = 1;
    } else {
        if (capabilities.threads) {
            ort.env.wasm.numThreads = navigator.hardwareConcurrency ?? 4;// 根据 CPU 核心数动态分配线程
        }
        if (capabilities.simd) {
            ort.env.wasm.simd = true;// 启用 SIMD 指令加速
        }
        ort.env.wasm.proxy = true;// 启用代理模式优化资源加载
    }
    // console.log('env', ort.env.wasm)
}

export const createInferenceSession = async (showAlert: any) => {
    prettierLog({
        msg: 'create InferenceSession',
        type: 'info'
    });
    if(!model) {
        const capabilities = await getBrowserCapabilities();
        configEnv(capabilities);

        const modelBuffer = await downloadModel(ModelTypeConstant.SUPER_RESOLUTION, downloadProgressHandle, showAlert);
        model = await ort.InferenceSession.create(modelBuffer, {
            // executionProviders: ['wasm'],
            executionProviders: [capabilities.webGPU ? 'webgpu' : 'wasm'],
            sessionOptions: {
                enableMemPattern: false,  // 禁用内存优化模式
                enableCpuMemArena: true,   // 启用CPU内存池
                memoryLimit: 12048,
            }
        });
    }
    prettierLog({
        msg: 'InferenceSession created',
        type: 'success'
    });
}

export const recreateInferenceSession = async (showAlert: any, showLoadingNotification: any) => {
    if (model && model.handler && model.handler.dispose) {
        model.handler.dispose();
        prettierLog({
            msg: "model dispose",
            type: "success"
        });
    }
    if (model && model.ioBinding) {
        model.ioBinding.dispose(); // 释放绑定的 GPU 缓冲区
        prettierLog({
            msg: "GPU Buffer dispose",
            type: "success"
        });
    }
    model = null;
    modelBasicInfo = getModel(ModelTypeConstant.SUPER_RESOLUTION);

    inputName = modelBasicInfo.inputName
    targetWidth = modelBasicInfo.targetWidth;
    targetHeight = modelBasicInfo.targetHeight;
    modelScale = modelBasicInfo.scale;
    loadingNotification = showLoadingNotification;

    await createInferenceSession();
}

const downloadProgressHandle = (progressNumber: number) => {
    // console.log("当前下载进度" + progressNumber);
    loadingNotification(progressNumber.toFixed(0), "Model Loading...", "Model Loaded");
}

const preProcess = async (imageUrl: string) => {
    prettierLog({
        msg: 'start preprocess',
        type: 'info'
    });

    let img = await loadImage(imageUrl);
    let resultWidth = img.width * modelScale;
    let resultHeight = img.height * modelScale;
    return new Promise((resolve, reject) => {
        try {
            const src = cv.imread(img);
            // eslint-disable-next-line camelcase
            const src_rgb = new cv.Mat();
            // 将图像从RGBA转换为RGB
            cv.cvtColor(src, src_rgb, cv.COLOR_RGBA2RGB);

            const channels = new cv.MatVector();
            cv.split(src_rgb, channels); // 分割通道
            const C = channels.size(); // 通道数
            const H = src_rgb.rows; // 图像高度
            const W = src_rgb.cols; // 图像宽度
            const chwArray = new Float32Array(C * H * W); // 创建新的数组来存储转换后的数据
            for (let c = 0; c < C; c++) {
                const channelData = channels.get(c).data; // 获取单个通道的数据
                for (let h = 0; h < H; h++) {
                    for (let w = 0; w < W; w++) {
                        chwArray[c * H * W + h * W + w] = channelData[h * W + w] / 255.0;// 存放归一化的数据
                    }
                }
            }

            const input = new ort.Tensor('float32', chwArray, [1, 3, img.height, img.width]);

            src_rgb.delete();
            src.delete();
            channels.delete(); // 清理内存

            prettierLog({
                msg: 'preprocess end',
                type: 'success'
            });
            resolve({
                inputTensor: input,
                resultWidth: resultWidth,
                resultHeight: resultHeight,
            });
        } catch (error) {
            reject(error);
        }
    })
}

const runModel = async (
    inputTensor: ort.Tensor,
    callback: (progress: {
        totalSteps: number;
        currentStep: number;
    }) => void
) => {
    prettierLog({
        msg: 'start run',
        type: 'info'
    });

    const inputDims = inputTensor.dims;
    const imageW = inputDims[3];
    const imageH = inputDims[2];

    const rOffset = 0
    const gOffset = imageW * imageH
    const bOffset = imageW * imageH * 2

    // 计算输出尺寸（动态根据modelScale）
    const outputDims = [
        inputDims[0],
        inputDims[1],
        inputDims[2] * modelScale,
        inputDims[3] * modelScale,
    ];

    // 初始化输出Tensor
    const outputTensor = new ort.Tensor(
        'float32',
        new Float32Array(outputDims[0] * outputDims[1] * outputDims[2] * outputDims[3]),
        outputDims
    );

    // 输出的相关参数
    const outImageW = outputDims[3]
    const outImageH = outputDims[2]
    const outROffset = 0
    const outGOffset = outImageW * outImageH
    const outBOffset = outImageW * outImageH * 2

    // 瓦片处理参数
    const tileSize = 64;
    const tilePadding = 6;
    const tileSizePre = tileSize - tilePadding * 2;

    const tileROffset = 0
    const tileGOffset = tileSize * tileSize
    const tileBOffset = tileSize * tileSize * 2

    // 计算瓦片数量
    const tilesx = Math.ceil(imageW / tileSizePre);
    const tilesy = Math.ceil(imageH / tileSizePre);
    const numTiles = tilesx * tilesy;
    let currentTile = 0;

    const { data } = inputTensor;

    for (let i = 0; i < tilesx; i++) {
        for (let j = 0; j < tilesy; j++) {
            const ti = Date.now();

            // 当前瓦片的实际尺寸（考虑边缘）
            const tileW = Math.min(tileSizePre, imageW - i * tileSizePre);
            const tileH = Math.min(tileSizePre, imageH - j * tileSizePre);

            // 创建带padding的瓦片数据
            const tileData = new Float32Array(tileSize * tileSize * 3);
            for (let xp = -tilePadding; xp < tileSizePre + tilePadding; xp++) {
                for (let yp = -tilePadding; yp < tileSizePre + tilePadding; yp++) {
                    let xim = i * tileSizePre + xp;
                    let yim = j * tileSizePre + yp;
                    xim = Math.max(0, Math.min(xim, imageW - 1));
                    yim = Math.max(0, Math.min(yim, imageH - 1));

                    const idx = xim + yim * imageW;
                    const xt = xp + tilePadding;
                    const yt = yp + tilePadding;

                    tileData[xt + yt * tileSize + tileROffset] = data[idx + rOffset];
                    tileData[xt + yt * tileSize + tileGOffset] = data[idx + gOffset];
                    tileData[xt + yt * tileSize + tileBOffset] = data[idx + bOffset];
                }
            }

            // 运行模型
            const tile = new ort.Tensor('float32', tileData, [1, 3, tileSize, tileSize]);
            const r = await model.run({ [inputName]: tile });
            const results = { output: r[model.outputNames[0]] };

            // 计算输出瓦片参数
            const outTileW = tileW * modelScale;
            const outTileH = tileH * modelScale;
            const outTileSize = tileSize * modelScale;
            const outTileSizePre = tileSizePre * modelScale;
            const outTilePadding = tilePadding * modelScale;

            const outTileROffset = 0
            const outTileGOffset = outTileSize * outTileSize
            const outTileBOffset = outTileSize * outTileSize * 2
            // 将结果写入输出Tensor
            for (let x = 0; x < outTileW; x++) {
                for (let y = 0; y < outTileH; y++) {
                    const xim = i * outTileSizePre + x;
                    const yim = j * outTileSizePre + y;
                    const idx = xim + yim * outImageW;

                    const xt = x + outTilePadding;
                    const yt = y + outTilePadding;

                    outputTensor.data[idx + outROffset] =
                        results.output.data[xt + yt * outTileSize + outTileROffset]
                    outputTensor.data[idx + outGOffset] =
                        results.output.data[xt + yt * outTileSize + outTileGOffset]
                    outputTensor.data[idx + outBOffset] =
                        results.output.data[xt + yt * outTileSize + outTileBOffset]
                }
            }

            // 进度日志
            currentTile++;
            const dt = Date.now() - ti;
            const remTime = (numTiles - currentTile) * dt;
            console.log(`Processed tile ${currentTile}/${numTiles} in ${dt}ms | Remaining: ~${remTime}ms`);
            callback({
                totalSteps: 100,
                currentStep: Math.round(100 * (currentTile / numTiles)),
            })
        }
    }

    prettierLog({
        msg: 'run end',
        type: 'success'
    });
    return outputTensor;
}

const postProcess = async (results: any, width: number, height: number) => {
    prettierLog({
        msg: 'start postProcess',
        type: 'info'
    });

    const outputData = results.data;

    const outputImgUint8Array = new Uint8ClampedArray(width * height * 4);
    const size = width * height

    for (let h = 0; h < height; h++) {
        for (let w = 0; w < width; w++) {
            const rgbaIndex = (h * width + w) * 4;
            for (let c = 0; c < 3; c++) {
                // RGB通道
                const chwIndex = c * size + h * width + w
                let pixelVal = outputData[chwIndex]
                pixelVal = Math.max(0, Math.min(1, pixelVal));
                outputImgUint8Array[rgbaIndex + c] = pixelVal * 255;
            }
            outputImgUint8Array[rgbaIndex + 3] = 255;
        }
    }

    // 转blob Url和ArrayBuffer
    let arrayBuffer = null;
    const imageData = new ImageData(outputImgUint8Array, width, height);
    const canvas = document.createElement('canvas');
    canvas.width = imageData.width;
    canvas.height = imageData.height;
    const ctx = canvas.getContext('2d');
    ctx.putImageData(imageData, 0, 0);
    const blobUrl = await new Promise((resolve) => {
        canvas.toBlob((blob) => {
            blobToArrayBuffer(blob, (arbuf) => {
                arrayBuffer = arbuf;
                resolve(URL.createObjectURL(blob));
            })
        }, 'image/png');
    });

    prettierLog({
        msg: 'postProcess end',
        type: 'success'
    });

    return { blobUrl: blobUrl, arrayBuffer: arrayBuffer };
}

export const modelIsLoaded = () => {
    return model !== null;
}
export const doImgEnhancer = async (
    imageUrl: string,
    callback: (progress: {
        totalSteps: number;
        currentStep: number;
    }) => void,
    showAlert: any
) => {
    return new Promise(async (resolve, reject) => {
        if (!model) await createInferenceSession(showAlert);
        const {inputTensor, resultWidth, resultHeight} = await preProcess(imageUrl);
        const results = await runModel(inputTensor, callback);

        const res = await postProcess(results, resultWidth, resultHeight);
        resolve(res);
    })
}
